// External libraries
import React from 'react';
import { arrayOf, string } from 'prop-types';
import { useStoreState } from '@/store';
import { FloatingLabelSelect } from '@checkout/react-components';

// Internal modules
import { getContent, getFieldError, logger, useMountLogging } from '@/lib';
import { getCardTypeSelectOptions } from './utils';
import { blurHandler, cardTypeChangeHandler } from './handlers';
import { logBlurOrFocusEvent as focusHandler } from '../../../Form/utils';
import { LoadingElement } from '@/components/Common';

const CardTypeSelector = () => {
  useMountLogging('CardTypeSelector');
  const allowedCardTypes = useStoreState(({ api }) => api.allowedCardTypes);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const cardNumberState = useStoreState(({ form }) => form.cardNumber);
  const { cardType } = cardNumberState;

  // The allowed card type are used to populate the select options.
  // If the API call hasn't returned yet we show the loading skeleton.
  if (!allowedCardTypes?.length) {
    return <LoadingElement />;
  }

  // Display error if card number field has error and card type is undefined
  // We are depending on cardNumber state here as cardType is a part of
  // cardNumber state object: cardNumber.cardType = ''
  const displayError = cardNumberState.displayError && !cardType;
  const error = getFieldError({
    errorCode: 'valueMissing',
    field: 'cardType',
    isValid: cardNumberState.isValid,
    value: cardType,
  });
  const selectOptions = getCardTypeSelectOptions(allowedCardTypes);

  // This fn is purely for logging purpose. We do not mark this field as
  // valid/invalid based on the return value of this fn
  const validationFn = value => {
    const isValid = allowedCardTypes.includes(value);

    if (!isValid) {
      // In theory we should not hit this log unless we have a bug
      logger.warn('selected_card_type_missing_in_allowedCardTypes');
    }

    // We really don't depend on this return value to validate this field
    return {};
  };

  return (
    <FloatingLabelSelect
      blurHandler={blurHandler}
      changeHandler={cardTypeChangeHandler}
      data-testid="card-type-selector"
      disabled={isFormSubmitting}
      displayError={displayError}
      emptyOption={{ enabled: true }}
      error={error}
      focusHandler={focusHandler}
      id="cardType"
      label={getContent('fieldLabels.cardType')}
      name="cardType"
      required={true}
      selectOptions={selectOptions}
      validationFn={validationFn}
      value={cardType}
    />
  );
};

CardTypeSelector.defaultProps = {
  allowedCardTypes: [],
};

CardTypeSelector.propTypes = {
  allowedCardTypes: arrayOf(string).isRequired,
};

export { CardTypeSelector };
